/*!
 * (C) Ionic http://ionicframework.com - MIT License
 */
import {
    ViewEncapsulation,
    Component,
    HostBinding,
    Input,
    OnInit,
    ElementRef,
    HostListener,
} from '@angular/core';

/**
 * @slot - Content is placed between the named slots if provided without a slot.
 * @slot icon-only - Should be used on an icon in a button that has no text.
 * @slot start - Content is placed to the left of the button text in LTR, and to the right in RTL.
 * @slot end - Content is placed to the right of the button text in LTR, and to the left in RTL.
 *
 * @part native - The native HTML button or anchor element that wraps all child elements.
 */

@Component({
    selector: 'bazis-button',
    templateUrl: './button.component.html',
    styleUrls: ['button.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class ButtonElement implements OnInit {
    @Input() size: 'xs' | 'small' | 'default' | 'large';

    @Input() fill: 'clear' | 'outline' | 'solid' = 'solid';

    @Input() active: boolean;

    @Input() disabled: boolean;

    @Input() download: string | undefined;

    @Input() href: string | undefined;

    @Input() target: '_blank' | '_self' | '_parent' | '_top';

    @Input() routerLink?: string;

    @Input() replaceUrl?: boolean;

    @Input() fragment?: string;

    @Input() type: 'button' | 'reset' | 'submit' = 'button';

    @HostBinding('class') get buttonStyle() {
        return {
            [`bazis-btn_${this.size}`]: !!this.size,
            'bazis-btn_has-icon-only': this.hasIconOnly,
            [`bazis-btn_${this.fill}`]: true,
            'bazis-btn--active': this.active,
            'bazis-btn--disabled': this.disabled,
        };
    }

    @HostBinding('tabindex') get focusableButton() {
        return '0';
    }

    @HostListener('click', ['$event']) public handleClick(event): void {
        if (this.type !== 'button') {
            // this button wants to specifically submit a form
            // climb up the dom to see if we're in a <form>
            // and if so, then use JS to submit it
            const form = this.el.nativeElement.closest('form');
            if (form) {
                event.preventDefault();
                const fakeButton = document.createElement('button');
                fakeButton.type = this.type;
                fakeButton.style.display = 'none';
                form.appendChild(fakeButton);
                fakeButton.click();
                fakeButton.remove();
            }
        }
    }

    nativeElement;

    constructor(private el: ElementRef) {}

    ngOnInit(): void {
        this.active = this.active !== undefined && this.active !== false;
        this.disabled = this.disabled !== undefined && this.disabled !== false;
        this.nativeElement = this.el.nativeElement;
    }

    get hasIconOnly() {
        return !!this.el.nativeElement.querySelector('[slot="icon-only"]');
    }
}
